import React, { useState, useEffect } from "react";
import axiosClient from "../../axios";
import { useUser } from "../../contexts/UserContext";

const UserProgress = () => {
  const [progressData, setProgressData] = useState(null);
  const { user, loading: userLoading } = useUser();
  const [error, setError] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);

  useEffect(() => {
    if (!userLoading && user) {
      setProgressLoading(true);
      axiosClient
        .get("/api/quiz-results")
        .then((response) => {
          setProgressData(response.data);
          setError(null);
        })
        .catch((err) => {
          setError("Unable to fetch your progress data.");
          console.error(err);
        })
        .finally(() => {
          setProgressLoading(false);
        });
    }
  }, [userLoading, user]);

  if (userLoading || progressLoading) {
    return (
      <div className="container py-4">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="container py-4">
        <div className="card shadow-sm border-0">
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-graph-up display-4 text-muted"></i>
            </div>
            <h5 className="text-muted">No Progress Data Available</h5>
            <p className="text-muted mb-4">
              Complete some quizzes to track your progress!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <h2 className="h5 mb-0">Your Learning Progress</h2>
          <p className="text-muted mb-0 small">
            Track your quiz performance and learning achievements
          </p>
        </div>

        <div className="card-body">
          {/* Overall Progress Summary */}
          <div className="card bg-light mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Overall Progress Summary</h5>
              <div className="row align-items-center">
                <div className="col-md-8">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Completion Rate</span>
                    <span className="fw-bold text-primary fs-5">
                      {progressData.overall_percentage}%
                    </span>
                  </div>
                  <div className="progress mb-3" style={{ height: "10px" }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{ width: `${progressData.overall_percentage}%` }}
                      aria-valuenow={progressData.overall_percentage}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">
                      {progressData.total_correct} correct answers
                    </small>
                    <small className="text-muted">
                      {progressData.total_questions} total questions
                    </small>
                  </div>
                </div>
                <div className="col-md-4 text-center">
                  <div
                    className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <span className="fs-4 fw-bold">
                      {progressData.overall_percentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress by Topic */}
          <div className="mb-4">
            <h5 className="mb-3">Progress by Topic</h5>
            {progressData.progress && progressData.progress.length > 0 ? (
              <div className="row">
                {progressData.progress.map((term) => (
                  <div key={term.term_id} className="col-md-6 col-lg-4 mb-3">
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body">
                        <h6
                          className="card-title text-truncate"
                          title={term.term_name}
                        >
                          {term.term_name}
                        </h6>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Progress</span>
                          <span
                            className={`fw-bold ${
                              term.percentage >= 80
                                ? "text-success"
                                : term.percentage >= 50
                                ? "text-warning"
                                : "text-danger"
                            }`}
                          >
                            {term.percentage}%
                          </span>
                        </div>

                        <div
                          className="progress mb-2"
                          style={{ height: "8px" }}
                        >
                          <div
                            className={`progress-bar ${
                              term.percentage >= 80
                                ? "bg-success"
                                : term.percentage >= 50
                                ? "bg-warning"
                                : "bg-danger"
                            }`}
                            role="progressbar"
                            style={{ width: `${term.percentage}%` }}
                            aria-valuenow={term.percentage}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>

                        <div className="d-flex justify-content-between">
                          <small className="text-muted">
                            {term.correct_answers}/{term.total_questions}{" "}
                            correct
                          </small>
                          <small className="text-muted">
                            {Math.round(
                              (term.correct_answers / term.total_questions) *
                                100
                            )}
                            %
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <i className="bi bi-journal-text display-6 text-muted mb-3"></i>
                <p className="text-muted">
                  No topic-specific progress data available.
                </p>
              </div>
            )}
          </div>

          {/* Performance Indicators */}
          <div className="card bg-light">
            <div className="card-body">
              <h6 className="card-title mb-3">Performance Indicators</h6>
              <div className="row text-center">
                <div className="col-4">
                  <div
                    className="bg-success rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                    style={{ width: "50px", height: "50px" }}
                  >
                    <i className="bi bi-check-lg text-white"></i>
                  </div>
                  <div>
                    <div className="fw-bold text-success">80-100%</div>
                    <small className="text-muted">Excellent</small>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    className="bg-warning rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                    style={{ width: "50px", height: "50px" }}
                  >
                    <i className="bi bi-dash-lg text-white"></i>
                  </div>
                  <div>
                    <div className="fw-bold text-warning">50-79%</div>
                    <small className="text-muted">Good</small>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    className="bg-danger rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                    style={{ width: "50px", height: "50px" }}
                  >
                    <i className="bi bi-x-lg text-white"></i>
                  </div>
                  <div>
                    <div className="fw-bold text-danger">0-49%</div>
                    <small className="text-muted">Needs Improvement</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProgress;
